import React, {useCallback, useEffect, useState} from 'react';
import ApiHelper from "../api/ApiHelper";
import {
    APourRole,
    DonneesConfiguration, ElementAnnexe,
    Localisation,
    Operation,
    Phase,
    Role,
    Situation, Statut,
    User
} from "../common/types/Types";

export const AuthContext = React.createContext<{
    user: User | null;
    isLoading: boolean;
    operations: Operation[];
    phases: Phase[];
    situations: Situation[];
    localisations: Localisation[];
    status: Statut[];
    formatter: Formatter;
    getOperation: (operationId: number) => Operation | undefined;
    getContactEmail: () => string;
    roles: Role[];
    additionalElements: ElementAnnexe[];
    updateUser: (user: User) => void;
    isAdmin: () => boolean;
    hasSeenMessage: (arg0: number)  => boolean;
    setSeenMessage: (arg0: number) => void;
    canViewOperationUsers: (operationId: number) => boolean;
    canCreateOperationUsers: (operationId: number) => boolean;
    canManageOperationUsers: (operationId: number) => boolean;
    getManagedOperationIds: () => number[];
    login: (loginData: any) => Promise<User>;
    logout: () => Promise<void>;
    isUserExpired: () => Promise<boolean>;
}>({
    user: null,
    isLoading: false,
    operations: [],
    phases: [],
    situations: [],
    localisations: [],
    status: [],
    formatter: null,
    getOperation: () => undefined,
    getContactEmail: () => '',
    roles: [],
    additionalElements: [],
    updateUser: (user: User) => undefined,
    isAdmin: () => false,
    hasSeenMessage: ()  => false,
    setSeenMessage: () => undefined,
    canViewOperationUsers: () => false,
    canCreateOperationUsers: () => false,
    canManageOperationUsers: () => false,
    getManagedOperationIds: () => [],
    login: () => Promise.resolve({} as User),
    logout: () => Promise.resolve(),
    isUserExpired: () => Promise.resolve(true),
});

export const ROLE_ADMIN_ID = 1000;
const VIEW_OPERATION_USERS_ROLE_IDS = [
    101, //Responsable Programmes
    102, //Secrétaire Programmes
    109, //Resp. Relation Client
    112, //MOTR
    133, //Manager de projet
];

const CREATE_OPERATION_EXTERNAL_USERS_ROLE_IDS = [
    101, //Responsable Programmes
    102, //Secrétaire Programmes
    109, //Resp. Relation Client
    112, //MOTR
    133, //Manager de projet
];
const MANAGE_OPERATION_EXTERNAL_USERS_ROLE_IDS = [
    101, //Responsable Programmes
    102, //Secrétaire Programmes
    109, //Resp. Relation Client
    112, //MOTR
    133, //Manager de projet
];

const isAdmin = (aUser: User) => {
    return !!aUser?.aPourRoles?.some((aPourRole: APourRole) => aPourRole.roleRefRole === ROLE_ADMIN_ID);
};

class Formatter {
    phases: Phase[] = [];
    roles: Role[] = [];
    localisations: Localisation[] = [];
    situations: Situation[] = [];
    status: Statut[] = [];
    additionalElements: ElementAnnexe[] = [];

    constructor(phases: Phase[], roles: Role[], localisations: Localisation[], situations: Situation[], status: Statut[], additionalElements: ElementAnnexe[]) {
        this.phases = phases;
        this.roles = roles;
        this.localisations = localisations;
        this.situations = situations;
        this.status = status;
        this.additionalElements = additionalElements;
    }

    getPhaseDescription(phaseId) {
        return this.phases.find(p => p.refPhase === phaseId)?.libPhase;
    }

    getStatusDescription(statusId) {
        return this.status.find(p => p.refStatut === statusId)?.libStatut;
    }

    getLocalisationsDescription(localisationIds) {
        return this.localisations.filter(l => localisationIds.includes(l.refLocalisation)).map(l => l.libLocalisation).join(', ');
    }

    getSituationsDescription(situationIds) {
        return this.situations.filter(l => situationIds.includes(l.refSituation)).map(l => l.libSituation).join(', ');
    }

    getRoleDescription(roleId) {
        return this.roles.find(r => r.refRole === roleId)?.libRole
    }

    getAdditionalElementDescription(additionalElementId) {
        return this.additionalElements.find(ae => ae.refElementAnnexe === additionalElementId)?.libElement
    }
}

interface AuthProviderProps {
    resetQueries: () => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({children, resetQueries}) => {
    const [user, setUser] = useState<User>(null);
    const [operations, setOperations] = useState<Operation[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [additionalElements, setAdditionalElements] = useState<ElementAnnexe[]>([]);
    const [phases, setPhases] = useState<Phase[]>([]);
    const [localisations, setLocalisations] = useState<Localisation[]>([]);
    const [situations, setSituations] = useState<Situation[]>([]);
    const [status, setStatus] = useState<Statut[]>([]);
    const [formatter, setFormatter] = useState<Formatter>(null);
    const [parameters, setParameters] = useState<DonneesConfiguration[]>([]);
    const [isLoading, setLoading] = useState(true);

    const logout = async () => {
        ApiHelper.logout();
        setUser(null);
        setTimeout(() => resetQueries(), 300);
    };

    const fetchOperations = useCallback(async (user) => {
        const userOperations = user?.aPourRoles.map(a => a.operation).sort((a, b) => {
            return a.nomOperation.localeCompare(b.nomOperation)
        }) || [];

        // filter to delete duplicate Operation when user have multiple ROLE
        const userOperationsUnique = userOperations.filter((value, index, self) => 
            index === self.findIndex((y) => (
                y.refOperation === value.refOperation
            )
        ))
        setOperations(userOperationsUnique);
    }, []);

    const fetchMasterData = useCallback(async (user) => {
        await fetchOperations(user);
        const [resPhases, resSituations, resLocalisations, resStatus, resRoles, resAdditionalElements, resParameters] = await Promise.all([
            ApiHelper.getPhases(),
            ApiHelper.getSituations(),
            ApiHelper.getLocalisations(),
            ApiHelper.getStatus(),
            ApiHelper.getRoles(),
            ApiHelper.getAdditionalElements(),
            ApiHelper.getParameters()
        ]);

        setPhases(resPhases.data);
        setLocalisations(resLocalisations.data);
        setSituations(resSituations.data);
        setStatus(resStatus.data);
        setRoles(resRoles.data);
        setAdditionalElements(resAdditionalElements.data);
        setParameters(resParameters.data);
        setFormatter(new Formatter(resPhases.data, resRoles.data, resLocalisations.data, resSituations.data, resStatus.data, resAdditionalElements.data));
    }, [fetchOperations]);

    useEffect(() => {
        ApiHelper.isAuthenticated().then((user) => {
            setUser(user);
            return fetchMasterData(user);
        }).then(() => setLoading(false)).catch(error => {
            console.error('Error checking if user is authenticated', error.message);
            setLoading(false);
        });
    }, [fetchMasterData]);


    return React.createElement(AuthContext.Provider, {
        value: {
            user,
            isLoading,
            isAdmin: () => {
                return isAdmin(user);
            },
            hasSeenMessage: (messageId: number)  => {
                return localStorage.getItem(`messages-${messageId}-${user.refUti}`) === 'true';
            },
            setSeenMessage: (messageId: number) => {
                localStorage.setItem(`messages-${messageId}-${user.refUti}`, 'true');
            },
            canViewOperationUsers: (operationId) => {
                if (!user) return false;
                if (isAdmin(user)) return true;

                const operationRole = user.aPourRoles?.find((aPourRole: APourRole) => aPourRole.operationRefOperation === operationId);

                return VIEW_OPERATION_USERS_ROLE_IDS.includes(operationRole?.roleRefRole);
            },
            canCreateOperationUsers: (operationId) => {
                if (!user) return false;
                if (isAdmin(user)) return true;

                const operationRole = user.aPourRoles?.find((aPourRole: APourRole) => aPourRole.operationRefOperation === operationId);
                return CREATE_OPERATION_EXTERNAL_USERS_ROLE_IDS.includes(operationRole?.roleRefRole);
            },
            canManageOperationUsers: (operationId) => {
                if (!user) return false;
                if (isAdmin(user)) return true;

                const operationRole = user.aPourRoles?.find((aPourRole: APourRole) => aPourRole.operationRefOperation === operationId);
                return MANAGE_OPERATION_EXTERNAL_USERS_ROLE_IDS.includes(operationRole?.roleRefRole);
            },
            getManagedOperationIds: () => {
                return user.aPourRoles.map(a => a.operationRefOperation);
            },
            // canManageUser: (aUser?: User) => {
            //     const managedOperationIds = user.aPourRoles
            //         .filter(a => aUser.aPourRoles?.map(a => a.operationRefOperation).includes(a.operationRefOperation))
            //         .map(a => a.operationRefOperation);
            // },
            formatter,
            getContactEmail: () => {
                return parameters.find(p => p.cle === 'CFG_ADR_MAIL_CONTACT')?.valeur;
            },
            getOperation: (operationId: number) => {
                return operations.find(operation => operation.refOperation === operationId);
            },
            operations,
            phases,
            situations,
            localisations,
            status,
            roles,
            additionalElements,
            updateUser: (user: User) => {
                setUser(user);
            },
            login: async (payload) => {
                const loginResponse = await ApiHelper.login(payload);

                if (loginResponse.data.mfaMailTokenSent) {
                    throw new Error('MFA_TOKEN_SENT');
                }
                if (loginResponse.data.mfaTokenInvalid) {
                    throw new Error('MFA_TOKEN_INVALID');
                }

                ApiHelper.saveAuthentication(JSON.stringify(loginResponse.data.token));
                const user = await ApiHelper.isAuthenticated();

                setUser(user);
                setLoading(false);
                await fetchMasterData(user);

                return user;
            },
            logout,
            isUserExpired: () => ApiHelper.isAuthenticated().then(user => {
                if (!user) {
                    setUser(user);
                }

                return user;
            })
        }
    }, children);
};
